import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useLayoutEffect } from 'react';
import { Box, Typography, Button, Skeleton, useTheme, } from '@mui/material';
import { replaceStylesheetGlobals } from '../../portal/theme/portalTheme';
import { dualModeLinkStyles } from '../utils/linkStyles';
const DEFAULT_MAX_LINES = 3;
const DescriptionRenderer = ({ description, maxLength = 200, maxLines = DEFAULT_MAX_LINES, showSeeMore = true, showMoreText = 'see more', showLessText = 'see less', stylesheet, sx = {}, isLoading, dualModeEnabled = false, }) => {
    const [expanded, setExpanded] = useState(false);
    const [canToggle, setCanToggle] = useState(false);
    const contentRef = useRef(null);
    const theme = useTheme();
    const body2Styles = theme.typography.body2;
    const buttonFontSize = body2Styles.fontSize ?? '0.875rem';
    const buttonLineHeight = body2Styles.lineHeight ?? 1.43;
    useLayoutEffect(() => {
        if (!contentRef.current || expanded || !showSeeMore) {
            setCanToggle(false);
            return;
        }
        const el = contentRef.current;
        const ro = new ResizeObserver(() => {
            setCanToggle(el.scrollHeight > el.clientHeight);
        });
        ro.observe(el);
        setCanToggle(el.scrollHeight > el.clientHeight);
        return () => ro.disconnect();
    }, [description, expanded, showSeeMore, maxLines]);
    const processDescriptionHtml = (html) => {
        if (!html)
            return html;
        return html.replace(/<a\s+([^>]*?)href\s*=\s*["']([^"']*?)["']([^>]*?)>/gi, (match, beforeHref, href, afterHref) => {
            const hasTarget = /target\s*=\s*["'][^"']*["']/i.test(match);
            if (!hasTarget) {
                return `<a ${beforeHref}href="${href}"${afterHref} target="_blank" rel="noopener noreferrer">`;
            }
            return match;
        });
    };
    if (!description) {
        if (isLoading) {
            return _jsx(Skeleton, { variant: "text", width: "100%" });
        }
        return null;
    }
    const useLineClamp = typeof maxLines === 'number' && maxLines > 0;
    const baseStyles = {
        color: 'inherit',
        '& p': {
            marginBlock: '0.5em',
            marginBlockStart: 0,
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        '& ul, & ol': {
            listStylePosition: 'outside',
            paddingLeft: '1.5em',
            marginTop: '0.5em',
            marginBottom: '0.5em',
        },
        '& ul': {
            listStyleType: 'disc',
        },
        '& ol': {
            listStyleType: 'decimal',
        },
        '& li': {
            display: 'list-item',
            marginLeft: 0,
            paddingLeft: 0,
            marginBottom: '0.25em',
        },
        '& li::marker': {
            color: 'inherit',
        },
        '& a': dualModeEnabled ? {
            ...dualModeLinkStyles,
            color: 'inherit',
        } : {
            color: 'inherit',
            textDecoration: 'underline',
            '&:hover': {
                opacity: 0.8,
            },
        },
        ...sx,
    };
    const descriptionStyles = {
        ...baseStyles,
        ...sx,
    };
    const toggleButtonSx = {
        color: replaceStylesheetGlobals(stylesheet, 'color_primary') || 'inherit',
        cursor: 'pointer',
        padding: 0,
        minWidth: 'auto',
        fontSize: buttonFontSize,
        lineHeight: buttonLineHeight,
        textTransform: 'none',
    };
    const clampContainerSx = useLineClamp && !expanded
        ? {
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minWidth: 0,
        }
        : undefined;
    const showSeeMoreButton = showSeeMore && canToggle && !expanded;
    const showSeeLessButton = showSeeMore && expanded;
    return (_jsx(Box, { children: _jsxs(Typography, { variant: "body2", sx: descriptionStyles, component: "div", display: "block", children: [_jsx(Box, { ref: contentRef, component: "div", display: "block", sx: clampContainerSx, onClick: (e) => {
                        const target = e.target;
                        if (target.tagName === 'A' || target.closest('a')) {
                            e.stopPropagation();
                            const link = target.tagName === 'A' ? target : target.closest('a');
                            const href = link?.getAttribute('href');
                            if (href)
                                window.open(href, '_blank', 'noopener,noreferrer');
                        }
                    }, dangerouslySetInnerHTML: { __html: processDescriptionHtml(description) } }), showSeeMoreButton && (_jsx(Button, { variant: "text", sx: toggleButtonSx, onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpanded(true);
                    }, children: ` (${showMoreText})` })), showSeeLessButton && (_jsx(Button, { variant: "text", sx: toggleButtonSx, onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpanded(false);
                    }, children: ` (${showLessText})` }))] }) }));
};
DescriptionRenderer.defaultProps = {
    maxLength: 200,
    maxLines: DEFAULT_MAX_LINES,
    showSeeMore: true,
    showMoreText: 'see more',
    showLessText: 'see less',
    stylesheet: undefined,
    sx: {},
};
export default DescriptionRenderer;
//# sourceMappingURL=DescriptionRenderer.js.map